import { useEffect } from 'react';
import { useUserStats } from './useUserStats';
import { useLevelUpStore } from '../stores/levelUpStore';
import { useUserStore } from '../stores/userStore';
import { useCategories } from './useCategories';
import { useUpgradesStore } from '../stores/upgradesStore';
import { getDisciplinesUnlockedBetween } from '../constants/disciplines';
import { calculateTierInfo } from '../utils/levelCalculation';
import type { LevelUpContext } from '../stores/levelUpStore';

export function useLevelUpDetection(context: LevelUpContext = 'immediate') {
  const { stats } = useUserStats();
  const { getCategoryName, getCategoryColor, getCategoryIcon } = useCategories();
  const { enqueueLevelUp } = useLevelUpStore();
  /**
   * ⚠️⚠️ THE BASELINE IS CLOUD-HELD AND IS THE WHOLE FIX. It used to be a
   * localStorage value plus an `isInitialMount` ref: the ref recorded whatever
   * `stats` said on the FIRST render, which — on a device with nothing cached —
   * is before the ~30 stores behind `useUserStats` have hydrated, i.e. all
   * zeros. The next pass then read `0 → 13` and celebrated levels earned months
   * ago. Living in `user_identity`, the baseline arrives WITH the account, so a
   * fresh device already knows where the user was.
   */
  const identityReady = useUserStore(s => s.initializedUserId !== null);
  const baseline = useUserStore(s => s.levelBaseline);
  const raiseLevelBaseline = useUserStore(s => s.raiseLevelBaseline);

  useEffect(() => {
    // Nothing is trustworthy until the identity blob (which carries the
    // baseline) has loaded. Firing before then is guessing.
    if (!identityReady) return;

    const categoryLevels: Record<string, number> = {};
    stats.categories.forEach(cat => { categoryLevels[cat.id] = cat.totalLevel; });

    /**
     * ⚠️⚠️ ESTABLISH SILENTLY, AND ONLY FROM A SNAPSHOT WITH REAL XP. A zero
     * snapshot is indistinguishable from "the stores have not loaded", and
     * persisting it would re-arm the exact false celebration this replaces —
     * only now written to the cloud, for every device.
     *
     * ⚠️ Accepted cost, the same one the earned-DATE guard takes: a brand-new
     * account's very first level-up is not celebrated, because establishing the
     * baseline and crossing the first level are the same event. Every level
     * after it fires normally.
     */
    if (!baseline) {
      if (stats.totalPoints > 0) raiseLevelBaseline(stats.totalLevel, categoryLevels);
      return;
    }

    const previousUserLevel = baseline.user;

    if (stats.totalLevel > previousUserLevel) {
      const userLevelInfo = {
        // ⚠️ `calculateTierInfo`, not a hand-rolled `% 100`. It is the same
        // function `stats.level` (the newLevel below) already comes from, so the
        // two ends of the span cannot disagree about where a tier starts — and
        // `levelsPerTier` is re-hydratable from the economy config, which a
        // literal 100 would silently ignore. Identical output at the default.
        oldLevel: calculateTierInfo(previousUserLevel).tierLevel,
        newLevel: stats.level,
        oldTotalLevel: previousUserLevel,
        newTotalLevel: stats.totalLevel,
        newPoints: stats.totalPoints
      };

      enqueueLevelUp(
        {
          type: 'user',
          ...userLevelInfo
        },
        context
      );
    }

    stats.categories.forEach(category => {
      // ⚠️ A domain absent from the baseline is one the high-water mark has
      // never seen — 0 is the right previous value for it.
      const previousCategoryLevel = baseline.domains[category.id] ?? 0;

      if (category.totalLevel > previousCategoryLevel) {
        const domainLevelInfo = {
          categoryId: category.id,
          categoryName: getCategoryName(category.id),
          categoryColor: getCategoryColor(category.id),
          categoryIcon: getCategoryIcon(category.id),
          // ⚠️ Same rule as the user span above — one source for the tier
          // convention, so `oldLevel` and `newLevel` can never disagree.
          oldLevel: calculateTierInfo(previousCategoryLevel).tierLevel,
          newLevel: category.level,
          oldTotalLevel: previousCategoryLevel,
          newTotalLevel: category.totalLevel,
          newPoints: category.points
        };

        enqueueLevelUp(
          {
            type: 'domain',
            ...domainLevelInfo
          },
          context
        );

        // Disciplines (DS3): a domain crossing previousCategoryLevel →
        // category.totalLevel may unlock disciplines whose threshold falls in
        // that window. Granting the unlock feeds the existing
        // UnlockCelebrationManager. Only genuine crossings reach here (initial
        // mount records a baseline without firing), so already-met thresholds
        // never re-celebrate. Skipped in bypass mode (everything's unlocked).
        const upgrades = useUpgradesStore.getState();
        const settings = upgrades.unlockSettings;
        if (settings?.systemEnabled && !settings.developerMode) {
          const crossed = getDisciplinesUnlockedBetween(
            category.id,
            previousCategoryLevel,
            category.totalLevel
          );
          crossed.forEach(def => {
            if (def.unlockKey) void upgrades.unlockFeature(def.unlockKey);
          });
        }
      }
    });

    // ⚠️ RAISE, never set. A transient dip (a store re-hydrating) must not pull
    // the mark down, or the next climb back would celebrate ground already won.
    raiseLevelBaseline(stats.totalLevel, categoryLevels);
  }, [stats.totalLevel, stats.totalPoints, stats.categories, context,
      identityReady, baseline, raiseLevelBaseline, enqueueLevelUp]);
}
